import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { addPrice, changePriceAmount, createPlan, updatePlan } from "../../api/billing";
import {
  BILLING_PERIODS,
  CURRENCIES,
  type AccountType,
  type AdminPlan,
  type AdminPrice,
  type BillingPeriod,
  type ProductLine,
  type Visibility,
} from "./types";
import { PRODUCT_LABEL } from "../../config/products";
import { ErrorText, Field, Modal, SaveButton, Toggle, inputClass, money, secondaryButtonClass, useAction } from "./ui";

type PriceRow = { key: string; billingPeriod: BillingPeriod; amount: string; currency: string; creditAmount: string };

const CATEGORY_OPTIONS = [
  { value: "creator", label: "Creator" },
  { value: "starter", label: "Starter" },
  { value: "growth", label: "Growth" },
  { value: "free", label: "Free" },
  { value: "addon", label: "Credit pack (one-time)" },
] as const;
type CategoryOption = (typeof CATEGORY_OPTIONS)[number]["value"];

const newPriceRow = (currency: string): PriceRow => ({
  key: crypto.randomUUID(),
  billingPeriod: "monthly",
  amount: "",
  currency,
  creditAmount: "",
});

const toIntOrNull = (value: string): number | null => (value.trim() === "" ? null : Number.parseInt(value, 10));

/**
 * Credits are granted once per payment, so a price that bills every N months
 * must carry N months of credits. Weekly / one-time have no monthly multiple.
 */
const MONTHS_PER_PAYMENT: Partial<Record<BillingPeriod, number>> = { monthly: 1, quarterly: 3, yearly: 12 };

const creditsFor = (monthlyCredits: number | null, period: BillingPeriod): string => {
  const months = MONTHS_PER_PAYMENT[period];
  return monthlyCredits !== null && months ? String(monthlyCredits * months) : "";
};

const creditsHint = (period: BillingPeriod): string | undefined => {
  if (period === "yearly") return "Given once a year at each payment — use 12 × the monthly credits.";
  if (period === "quarterly") return "Given once a quarter at each payment — use 3 × the monthly credits.";
  return undefined;
};

const CancelButton: React.FC<Readonly<{ busy: boolean; onClose: () => void }>> = ({ busy, onClose }) => (
  <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onClose}>
    Cancel
  </button>
);

function validatePriceRows(rows: PriceRow[], category: CategoryOption): string | null {
  if (rows.length === 0) return "Add at least one price.";
  for (const row of rows) {
    const amount = Number(row.amount);
    if (row.amount.trim() === "" || !Number.isFinite(amount) || amount < 0) return "Every price needs a valid amount.";
    if (category !== "free" && amount <= 0) return "Paid plan prices must be more than 0.";
    if (category === "free" && amount !== 0) return "Free plan prices must be 0.";
  }
  const isOneTime = rows.map((r) => r.billingPeriod === "one_time");
  if (isOneTime.some(Boolean) && !isOneTime.every(Boolean)) return "A plan is either one-time or recurring, not both.";
  if (category === "addon" && !isOneTime.every(Boolean)) return "Credit packs use the one-time period.";
  return null;
}

/** Create a plan with its prices (provider plans are created by the server). */
export const CreatePlanModal: React.FC<
  Readonly<{ productLine: ProductLine; onClose: () => void; onSaved: () => void }>
> = ({ productLine, onClose, onSaved }) => {
  const defaultCurrency = productLine === "DRAWPOST_ADS" ? "inr" : "usd";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<CategoryOption>("starter");
  const [accountType, setAccountType] = useState<AccountType>("ALL");
  const [visibility, setVisibility] = useState<Visibility>("PUBLIC");
  const [trialDays, setTrialDays] = useState("");
  const [rows, setRows] = useState<PriceRow[]>([newPriceRow(defaultCurrency)]);
  const { busy, error, setError, run } = useAction();

  const updateRow = (key: string, patch: Partial<PriceRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: string) => setRows((prev) => prev.filter((r) => r.key !== key));

  /** Switching a row to yearly/quarterly fills its credits from the monthly row. */
  const changePeriod = (row: PriceRow, period: BillingPeriod) => {
    const monthly = rows.find((r) => r.key !== row.key && r.billingPeriod === "monthly" && r.creditAmount.trim() !== "");
    const monthlyCredits = monthly ? Number.parseInt(monthly.creditAmount, 10) : null;
    const filled = creditsFor(monthlyCredits, period);
    updateRow(row.key, { billingPeriod: period, ...(filled ? { creditAmount: filled } : {}) });
  };

  const save = async () => {
    if (!name.trim()) return setError("Name is required.");
    const rowError = validatePriceRows(rows, category);
    if (rowError) return setError(rowError);
    const saved = await run(() =>
      createPlan({
        name: name.trim(),
        description: description.trim() || undefined,
        productLine,
        category,
        accountType,
        visibility,
        trialDays: toIntOrNull(trialDays) ?? undefined,
        prices: rows.map((r) => ({
          billingPeriod: r.billingPeriod,
          amount: Number(r.amount),
          currency: r.currency,
          creditAmount: toIntOrNull(r.creditAmount),
        })),
      })
    );
    if (saved) onSaved();
  };

  return (
    <Modal
      title={`New ${PRODUCT_LABEL[productLine]} plan`}
      onClose={onClose}
      busy={busy}
      width="xl"
      footer={
        <>
          <CancelButton busy={busy} onClose={onClose} />
          <SaveButton busy={busy} onClick={save}>
            Create plan
          </SaveButton>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Name">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          <Field label="Category">
            <select className={inputClass} value={category} onChange={(e) => setCategory(e.target.value as CategoryOption)}>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Description" className="md:col-span-2">
            <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
          </Field>
          <Field label="Sold to">
            <select className={inputClass} value={accountType} onChange={(e) => setAccountType(e.target.value as AccountType)}>
              <option value="ALL">Everyone</option>
              <option value="CLIENT">Individual accounts only</option>
              <option value="AGENCY">Agencies only</option>
            </select>
          </Field>
          <Field label="Visibility" hint="Private plans are only shown to users they are assigned to.">
            <select className={inputClass} value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)}>
              <option value="PUBLIC">Public (in the plans page)</option>
              <option value="PRIVATE">Private (assigned users only)</option>
            </select>
          </Field>
          <Field label="Free trial days" hint="Optional; applies to every price.">
            <input className={inputClass} type="number" min={0} value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Prices</p>
          {rows.map((row) => (
            <div key={row.key} className="grid grid-cols-2 gap-2 md:grid-cols-[1fr_1fr_1fr_1fr_auto] md:items-end">
              <Field label="Billing period">
                <select
                  className={inputClass}
                  value={row.billingPeriod}
                  onChange={(e) => changePeriod(row, e.target.value as BillingPeriod)}
                >
                  {BILLING_PERIODS.map((p) => (
                    <option key={p} value={p}>
                      {p.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Amount">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  step="0.01"
                  value={row.amount}
                  onChange={(e) => updateRow(row.key, { amount: e.target.value })}
                />
              </Field>
              <Field label="Currency">
                <select className={inputClass} value={row.currency} onChange={(e) => updateRow(row.key, { currency: e.target.value })}>
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c.toUpperCase()}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Credits per payment" hint={creditsHint(row.billingPeriod)}>
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={row.creditAmount}
                  onChange={(e) => updateRow(row.key, { creditAmount: e.target.value })}
                />
              </Field>
              <button
                type="button"
                aria-label="Remove price"
                disabled={rows.length === 1}
                onClick={() => removeRow(row.key)}
                className="mb-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-30"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={() => setRows((prev) => [...prev, newPriceRow(defaultCurrency)])}
          >
            <Plus className="w-4 h-4" /> Add price
          </button>
          <p className="text-xs text-gray-400">
            Recurring prices create a payment-provider plan. Plan limits (DrawPost) are set after creating, from Settings.
          </p>
        </div>
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};

const MAX_TRIAL_DAYS = 365;

/** Distinct trial lengths (0 = none) across a plan's live recurring prices. */
const recurringTrials = (plan: AdminPlan): number[] =>
  Array.from(
    new Set(plan.prices.filter((p) => p.isActive && p.billingPeriod !== "one_time").map((p) => p.trialDays ?? 0))
  );

const trialHint = (applies: boolean, isVariant: boolean, trials: number[]): string => {
  if (!applies) return isVariant ? "Offer copies have no trial of their own." : "Free plans and credit packs have no trial.";
  if (trials.length > 1) return `Prices differ now (${trials.join(", ")} days); saving sets all of them.`;
  return "0 = no trial. Applies to every price; current subscribers are not affected.";
};

/** Edit name, description, audience, visibility, trial and on/off. */
export const EditPlanModal: React.FC<Readonly<{ plan: AdminPlan; onClose: () => void; onSaved: () => void }>> = ({
  plan,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description ?? "");
  const [accountType, setAccountType] = useState<AccountType>(plan.accountType);
  const [visibility, setVisibility] = useState<Visibility>(plan.visibility);
  const [isActive, setIsActive] = useState(plan.isActive);
  const { busy, error, setError, run } = useAction();
  const isVariant = plan.basePlanId !== null;
  const trialApplies =
    !isVariant && plan.category !== "FREE" && !plan.prices.some((p) => p.billingPeriod === "one_time");
  const trials = recurringTrials(plan);
  const initialTrial = trials.length === 1 ? String(trials[0]) : "";
  const [trialDays, setTrialDays] = useState(initialTrial);

  const save = async () => {
    if (!name.trim()) return setError("Name is required.");
    // Sent only when changed, so other edits never overwrite the prices' trials.
    const trialChanged = trialApplies && trialDays.trim() !== initialTrial;
    const trialValue = trialDays.trim() === "" ? 0 : Number(trialDays);
    if (trialChanged && (!Number.isInteger(trialValue) || trialValue < 0 || trialValue > MAX_TRIAL_DAYS)) {
      return setError(`Free trial days must be a whole number from 0 to ${MAX_TRIAL_DAYS}.`);
    }
    const saved = await run(() =>
      updatePlan(plan.id, {
        name: name.trim(),
        description: description.trim() || null,
        accountType,
        visibility,
        isActive,
        ...(trialChanged ? { trialDays: trialValue } : {}),
      })
    );
    if (saved) onSaved();
  };

  return (
    <Modal
      title={`Edit ${plan.name}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <CancelButton busy={busy} onClose={onClose} />
          <SaveButton busy={busy} onClick={save}>
            Save
          </SaveButton>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </Field>
        <Field label="Sold to">
          <select className={inputClass} value={accountType} onChange={(e) => setAccountType(e.target.value as AccountType)}>
            <option value="ALL">Everyone</option>
            <option value="CLIENT">Individual accounts only</option>
            <option value="AGENCY">Agencies only</option>
          </select>
        </Field>
        <Field label="Description" className="md:col-span-2">
          <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
        </Field>
        <Field label="Visibility" hint={isVariant ? "Discounted copies are always private." : undefined}>
          <select
            className={inputClass}
            value={visibility}
            disabled={isVariant}
            onChange={(e) => setVisibility(e.target.value as Visibility)}
          >
            <option value="PUBLIC">Public</option>
            <option value="PRIVATE">Private (assigned users only)</option>
          </select>
        </Field>
        <Field label="Free trial days" hint={trialHint(trialApplies, isVariant, trials)}>
          <input
            className={inputClass}
            type="number"
            min={0}
            max={MAX_TRIAL_DAYS}
            step={1}
            value={trialDays}
            disabled={!trialApplies}
            placeholder={trials.length > 1 ? "Mixed" : "0"}
            onChange={(e) => setTrialDays(e.target.value)}
          />
        </Field>
        <div className="pt-5">
          <Toggle
            checked={isActive}
            onChange={setIsActive}
            label="Plan active"
            text={isActive ? "Active — can be bought" : "Off — hidden from new buyers"}
          />
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-400">Switching a plan off never cancels anyone already subscribed.</p>
      <div className="mt-3">
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};

/** Add another period/currency price to an existing plan. */
export const AddPriceModal: React.FC<Readonly<{ plan: AdminPlan; onClose: () => void; onSaved: () => void }>> = ({
  plan,
  onClose,
  onSaved,
}) => {
  const isOneTimePlan = plan.prices.some((p) => p.billingPeriod === "one_time");
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(isOneTimePlan ? "one_time" : "monthly");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(plan.prices[0]?.currency ?? "inr");
  const monthlyCredits = (() => {
    const monthly = plan.prices.find((p) => p.isActive && p.billingPeriod === "monthly");
    return monthly?.creditAmount ?? null;
  })();
  const initialCredits = isOneTimePlan ? String(plan.prices[0]?.creditAmount ?? "") : creditsFor(monthlyCredits, "monthly");
  const [creditAmount, setCreditAmount] = useState(initialCredits);
  const [creditsEdited, setCreditsEdited] = useState(false);
  const { busy, error, setError, run } = useAction();

  const changePeriod = (period: BillingPeriod) => {
    setBillingPeriod(period);
    // Until the admin types their own number, keep credits in step with the period.
    if (!creditsEdited) setCreditAmount(creditsFor(monthlyCredits, period));
  };

  const save = async () => {
    const value = Number(amount);
    if (amount.trim() === "" || !Number.isFinite(value) || value < 0) return setError("Enter a valid amount.");
    const saved = await run(() =>
      addPrice(plan.id, { billingPeriod, amount: value, currency, creditAmount: toIntOrNull(creditAmount) })
    );
    if (saved) onSaved();
  };

  return (
    <Modal
      title={`Add price to ${plan.name}`}
      onClose={onClose}
      busy={busy}
      width="md"
      footer={
        <>
          <CancelButton busy={busy} onClose={onClose} />
          <SaveButton busy={busy} onClick={save}>
            Add price
          </SaveButton>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Billing period">
          <select
            className={inputClass}
            value={billingPeriod}
            disabled={isOneTimePlan}
            onChange={(e) => changePeriod(e.target.value as BillingPeriod)}
          >
            {BILLING_PERIODS.filter((p) => (isOneTimePlan ? p === "one_time" : p !== "one_time")).map((p) => (
              <option key={p} value={p}>
                {p.replace("_", " ")}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Currency">
          <select className={inputClass} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c.toUpperCase()}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount">
          <input className={inputClass} type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Credits per payment" hint={creditsHint(billingPeriod)}>
          <input
            className={inputClass}
            type="number"
            min={0}
            value={creditAmount}
            onChange={(e) => {
              setCreditsEdited(true);
              setCreditAmount(e.target.value);
            }}
          />
        </Field>
      </div>
      <div className="mt-3">
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};

/** New amount for a price — existing subscribers keep paying what they signed up for. */
export const ChangeAmountModal: React.FC<
  Readonly<{ plan: AdminPlan; price: AdminPrice; onClose: () => void; onSaved: () => void }>
> = ({ plan, price, onClose, onSaved }) => {
  const [amount, setAmount] = useState(price.amount);
  const { busy, error, setError, run } = useAction();

  const save = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return setError("Enter an amount above 0.");
    const saved = await run(() => changePriceAmount(price.id, value));
    if (saved) onSaved();
  };

  return (
    <Modal
      title={`Change ${plan.name} price`}
      onClose={onClose}
      busy={busy}
      width="md"
      footer={
        <>
          <CancelButton busy={busy} onClose={onClose} />
          <SaveButton busy={busy} onClick={save}>
            Change price
          </SaveButton>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Current: <strong>{money(price.amount, price.currency)}</strong> / {price.billingPeriod.replace("_", " ")}
        </p>
        <Field label={`New amount (${price.currency.toUpperCase()})`}>
          <input className={inputClass} type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        {price.liveSubscribers > 0 && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {price.liveSubscribers} subscriber{price.liveSubscribers === 1 ? "" : "s"} stay on the current amount; only new
            purchases pay the new one.
          </p>
        )}
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};
